import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Chart } from "react-google-charts";

// 数値フォーマット
const formatNumber = (num) => {
  const n = Number(num) || 0;
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
};

const formatCurrency = (num) => `¥${Math.round(Number(num) || 0).toLocaleString()}`;

const STATUS_LABELS = {
  ACTIVE: { label: "配信中", className: "status-active" },
  PAUSED: { label: "停止中", className: "status-paused" },
  ARCHIVED: { label: "アーカイブ", className: "status-archived" },
  DELETED: { label: "削除済み", className: "status-archived" },
};

const OBJECTIVE_LABELS = {
  CONVERSIONS: "コンバージョン",
  OUTCOME_SALES: "売上",
  OUTCOME_LEADS: "リード",
  OUTCOME_TRAFFIC: "トラフィック",
  OUTCOME_AWARENESS: "認知度",
  OUTCOME_ENGAGEMENT: "エンゲージメント",
  OUTCOME_APP_PROMOTION: "アプリ促進",
  REACH: "リーチ",
  TRAFFIC: "トラフィック",
  ENGAGEMENT: "エンゲージメント",
  AWARENESS: "認知度",
  LEAD_GENERATION: "リード獲得",
  MESSAGES: "メッセージ",
  VIDEO_VIEWS: "動画再生",
};

const CREATIVE_SORT_OPTIONS = [
  { value: "spend", label: "広告費" },
  { value: "impressions", label: "インプレッション" },
  { value: "clicks", label: "クリック" },
  { value: "conversions", label: "コンバージョン" },
  { value: "ctr", label: "CTR" },
  { value: "cpc", label: "CPC" },
  { value: "roas", label: "ROAS" },
];

function MetaAdsPage() {
  const [selectedDays, setSelectedDays] = useState(30);
  const [activeTab, setActiveTab] = useState("overview");
  const [campaignFilter, setCampaignFilter] = useState("ALL");

  const [campaigns, setCampaigns] = useState([]);
  const [dailyData, setDailyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // クリエイティブ展開状態: { [campaignId]: { open, loading, data, error, sortBy } }
  const [creativeStates, setCreativeStates] = useState({});

  // ===== API呼び出し =====
  const fetchCampaigns = useCallback(async (days) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const response = await fetch(
      `${apiUrl}/api/get_meta_ads_data/?days=${days}`,
      {
        method: "GET",
        credentials: "include",
      }
    );
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "キャンペーンデータの取得に失敗しました");
    }
    return data;
  }, []);

  const fetchDaily = useCallback(async (days) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const response = await fetch(`${apiUrl}/api/get_meta_ads_daily_data/`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ days }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || "日別データの取得に失敗しました");
    }
    return data;
  }, []);

  const loadAll = useCallback(
    async (days) => {
      setLoading(true);
      setError(null);
      const [campaignsRes, dailyRes] = await Promise.allSettled([
        fetchCampaigns(days),
        fetchDaily(days),
      ]);

      if (campaignsRes.status === "fulfilled") {
        setCampaigns(
          Array.isArray(campaignsRes.value) ? campaignsRes.value : []
        );
      } else {
        setCampaigns([]);
        console.error(campaignsRes.reason);
      }

      if (dailyRes.status === "fulfilled") {
        setDailyData(dailyRes.value);
      } else {
        setDailyData(null);
        console.error(dailyRes.reason);
      }

      const errors = [];
      if (campaignsRes.status === "rejected") {
        errors.push(
          `キャンペーン: ${campaignsRes.reason?.message || "取得失敗"}`
        );
      }
      if (dailyRes.status === "rejected") {
        errors.push(`日別データ: ${dailyRes.reason?.message || "取得失敗"}`);
      }
      setError(errors.length ? errors.join(" / ") : null);
      setLoading(false);
    },
    [fetchCampaigns, fetchDaily]
  );

  useEffect(() => {
    loadAll(selectedDays);
  }, [selectedDays, loadAll]);

  // 期間変更時はクリエイティブのキャッシュをクリア（取得済みのものを再取得させる）
  useEffect(() => {
    setCreativeStates({});
  }, [selectedDays]);

  const fetchCreatives = useCallback(
    async (campaignId) => {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}/api/get_meta_ads_creatives/?campaign_id=${campaignId}&days=${selectedDays}`,
        { method: "GET", credentials: "include" }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "クリエイティブの取得に失敗しました");
      }
      return data;
    },
    [selectedDays]
  );

  const toggleCreatives = useCallback(
    async (campaignId) => {
      setCreativeStates((prev) => {
        const current = prev[campaignId];
        // 既に開いてデータあり → 閉じる
        if (current?.open && current?.data) {
          return { ...prev, [campaignId]: { ...current, open: false } };
        }
        // 既にデータあり、閉じている → 開く
        if (current?.data) {
          return { ...prev, [campaignId]: { ...current, open: true } };
        }
        // 初回 → ロード開始
        return {
          ...prev,
          [campaignId]: { open: true, loading: true, sortBy: "spend" },
        };
      });

      const existing = creativeStates[campaignId];
      if (existing?.data) return;

      try {
        const data = await fetchCreatives(campaignId);
        setCreativeStates((prev) => ({
          ...prev,
          [campaignId]: {
            open: true,
            loading: false,
            data,
            sortBy: prev[campaignId]?.sortBy || "spend",
          },
        }));
      } catch (err) {
        setCreativeStates((prev) => ({
          ...prev,
          [campaignId]: {
            open: true,
            loading: false,
            error: err.message,
            sortBy: "spend",
          },
        }));
      }
    },
    [creativeStates, fetchCreatives]
  );

  const setCreativeSort = useCallback((campaignId, sortBy) => {
    setCreativeStates((prev) => ({
      ...prev,
      [campaignId]: { ...prev[campaignId], sortBy },
    }));
  }, []);

  // ===== 派生データ =====
  const summary = dailyData?.summary || null;
  const dailyBreakdown = dailyData?.daily_breakdown || [];

  const filteredCampaigns = useMemo(() => {
    if (campaignFilter === "ALL") return campaigns;
    return campaigns.filter((c) => c.status === campaignFilter);
  }, [campaigns, campaignFilter]);

  // チャートデータ
  const trendChartData = useMemo(() => {
    const header = ["日付", "広告費", "コンバージョン"];
    const rows = dailyBreakdown.map((d) => [
      new Date(d.date).toLocaleDateString("ja-JP", {
        month: "numeric",
        day: "numeric",
      }),
      d.spend,
      d.conversions,
    ]);
    return [header, ...rows];
  }, [dailyBreakdown]);

  const reachChartData = useMemo(() => {
    const header = ["日付", "インプレッション", "クリック"];
    const rows = dailyBreakdown.map((d) => [
      new Date(d.date).toLocaleDateString("ja-JP", {
        month: "numeric",
        day: "numeric",
      }),
      d.impressions,
      d.clicks,
    ]);
    return [header, ...rows];
  }, [dailyBreakdown]);

  const campaignSpendData = useMemo(() => {
    const header = ["キャンペーン", "広告費"];
    const rows = campaigns
      .filter((c) => (c.insights?.spend || 0) > 0)
      .map((c) => [c.name, c.insights.spend]);
    return [header, ...rows];
  }, [campaigns]);

  const kpiCards = summary
    ? [
        {
          key: "spend",
          label: "広告費",
          icon: "fa-yen-sign",
          value: formatCurrency(summary.spend),
          color: "purple",
        },
        {
          key: "impressions",
          label: "インプレッション",
          icon: "fa-eye",
          value: formatNumber(summary.impressions),
          color: "blue",
        },
        {
          key: "clicks",
          label: "クリック",
          icon: "fa-arrow-pointer",
          value: formatNumber(summary.clicks),
          color: "cyan",
        },
        {
          key: "conversions",
          label: "コンバージョン",
          icon: "fa-bullseye",
          value: (summary.conversions || 0).toLocaleString(),
          color: "green",
        },
        {
          key: "ctr",
          label: "CTR",
          icon: "fa-percent",
          value: `${(summary.ctr || 0).toFixed(2)}%`,
          color: "orange",
        },
        {
          key: "cpc",
          label: "CPC",
          icon: "fa-coins",
          value: formatCurrency(summary.cpc),
          color: "pink",
        },
        {
          key: "cpa",
          label: "CPA",
          icon: "fa-tag",
          value: formatCurrency(summary.cpa),
          color: "red",
        },
        {
          key: "cvr",
          label: "コンバージョン率",
          icon: "fa-chart-line",
          value: `${(summary.conversion_rate || 0).toFixed(2)}%`,
          color: "teal",
        },
      ]
    : [];

  return (
    <div className="meta-ads-page">
      {/* ヘッダー */}
      <div className="meta-ads-header">
        <div className="header-title">
          <h2>
            <i className="fa-brands fa-meta"></i>
            Meta広告ダッシュボード
          </h2>
          <p className="subtitle">
            Facebook / Instagram広告のパフォーマンスを集計・分析
          </p>
        </div>

        <div className="header-actions">
          <div className="period-selector">
            {[7, 14, 30, 90].map((days) => (
              <button
                key={days}
                className={`period-btn ${selectedDays === days ? "active" : ""}`}
                onClick={() => setSelectedDays(days)}
              >
                {days}日
              </button>
            ))}
          </div>
          <button
            className="refresh-btn"
            onClick={() => loadAll(selectedDays)}
            title="再取得"
          >
            <i className="fa-solid fa-rotate"></i>
          </button>
        </div>
      </div>

      {/* タブ */}
      <div className="meta-tabs">
        <button
          className={`tab ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          <i className="fa-solid fa-chart-pie"></i>
          概要
        </button>
        <button
          className={`tab ${activeTab === "campaigns" ? "active" : ""}`}
          onClick={() => setActiveTab("campaigns")}
        >
          <i className="fa-solid fa-bullhorn"></i>
          キャンペーン
        </button>
        <button
          className={`tab ${activeTab === "daily" ? "active" : ""}`}
          onClick={() => setActiveTab("daily")}
        >
          <i className="fa-solid fa-calendar-days"></i>
          日別データ
        </button>
      </div>

      {/* エラー表示 */}
      {error && (
        <div className="error-banner">
          <i className="fa-solid fa-triangle-exclamation"></i>
          <div>
            <p className="error-title">データを取得できませんでした</p>
            <p className="error-detail">{error}</p>
          </div>
          <button onClick={() => loadAll(selectedDays)}>
            <i className="fa-solid fa-rotate"></i>
            再試行
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading-skeleton">
          <div className="skeleton-row" />
          <div className="skeleton-row" />
          <div className="skeleton-row" />
        </div>
      ) : (
        <>
          {/* ===== 概要タブ ===== */}
          {activeTab === "overview" && (
            <div className="tab-content">
              {kpiCards.length > 0 ? (
                <>
                  <div className="kpi-grid">
                    {kpiCards.map((kpi) => (
                      <div key={kpi.key} className={`kpi-card kpi-${kpi.color}`}>
                        <div className="kpi-icon">
                          <i className={`fa-solid ${kpi.icon}`}></i>
                        </div>
                        <div className="kpi-body">
                          <p className="kpi-label">{kpi.label}</p>
                          <p className="kpi-value">{kpi.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="charts-grid">
                    <div className="chart-card chart-wide">
                      <div className="chart-header">
                        <h3>
                          <i className="fa-solid fa-chart-area"></i>
                          広告費 & コンバージョン推移
                        </h3>
                      </div>
                      {trendChartData.length > 1 ? (
                        <Chart
                          chartType="AreaChart"
                          width="100%"
                          height="320px"
                          data={trendChartData}
                          options={{
                            backgroundColor: "transparent",
                            chartArea: {
                              left: 60,
                              top: 20,
                              right: 60,
                              bottom: 50,
                              width: "85%",
                              height: "75%",
                            },
                            colors: ["#a78bfa", "#34d399"],
                            legend: {
                              position: "top",
                              alignment: "end",
                              textStyle: { color: "#cbd5e1", fontSize: 12 },
                            },
                            hAxis: {
                              textStyle: { fontSize: 11, color: "#94a3b8" },
                              gridlines: { color: "transparent" },
                            },
                            vAxes: {
                              0: {
                                textStyle: { fontSize: 11, color: "#a78bfa" },
                                gridlines: { color: "rgba(148, 163, 184, 0.08)" },
                                baselineColor: "rgba(148, 163, 184, 0.15)",
                              },
                              1: {
                                textStyle: { fontSize: 11, color: "#34d399" },
                                gridlines: { color: "transparent" },
                                baselineColor: "transparent",
                              },
                            },
                            series: {
                              0: { targetAxisIndex: 0, areaOpacity: 0.25 },
                              1: { targetAxisIndex: 1, areaOpacity: 0.25 },
                            },
                          }}
                        />
                      ) : (
                        <p className="empty-msg">データがありません</p>
                      )}
                    </div>

                    <div className="chart-card">
                      <div className="chart-header">
                        <h3>
                          <i className="fa-solid fa-chart-pie"></i>
                          キャンペーン別広告費
                        </h3>
                      </div>
                      {campaignSpendData.length > 1 ? (
                        <Chart
                          chartType="PieChart"
                          width="100%"
                          height="320px"
                          data={campaignSpendData}
                          options={{
                            backgroundColor: "transparent",
                            chartArea: {
                              left: 20,
                              top: 20,
                              width: "100%",
                              height: "80%",
                            },
                            colors: [
                              "#a78bfa",
                              "#f472b6",
                              "#60a5fa",
                              "#34d399",
                              "#fb923c",
                              "#22d3ee",
                              "#facc15",
                              "#fb7185",
                            ],
                            legend: {
                              position: "right",
                              textStyle: { fontSize: 11, color: "#cbd5e1" },
                            },
                            pieHole: 0.5,
                            pieSliceText: "percentage",
                            pieSliceTextStyle: { fontSize: 11, color: "#fff" },
                            pieSliceBorderColor: "transparent",
                          }}
                        />
                      ) : (
                        <p className="empty-msg">データがありません</p>
                      )}
                    </div>

                    <div className="chart-card chart-wide">
                      <div className="chart-header">
                        <h3>
                          <i className="fa-solid fa-chart-line"></i>
                          インプレッション & クリック推移
                        </h3>
                      </div>
                      {reachChartData.length > 1 ? (
                        <Chart
                          chartType="LineChart"
                          width="100%"
                          height="280px"
                          data={reachChartData}
                          options={{
                            backgroundColor: "transparent",
                            chartArea: {
                              left: 70,
                              top: 20,
                              right: 70,
                              bottom: 50,
                              width: "85%",
                              height: "75%",
                            },
                            colors: ["#60a5fa", "#f472b6"],
                            legend: {
                              position: "top",
                              alignment: "end",
                              textStyle: { color: "#cbd5e1", fontSize: 12 },
                            },
                            curveType: "function",
                            hAxis: {
                              textStyle: { fontSize: 11, color: "#94a3b8" },
                              gridlines: { color: "transparent" },
                            },
                            vAxes: {
                              0: {
                                textStyle: { fontSize: 11, color: "#60a5fa" },
                                gridlines: { color: "rgba(148, 163, 184, 0.08)" },
                                baselineColor: "rgba(148, 163, 184, 0.15)",
                              },
                              1: {
                                textStyle: { fontSize: 11, color: "#f472b6" },
                                gridlines: { color: "transparent" },
                                baselineColor: "transparent",
                              },
                            },
                            series: {
                              0: { targetAxisIndex: 0, lineWidth: 3 },
                              1: { targetAxisIndex: 1, lineWidth: 3 },
                            },
                          }}
                        />
                      ) : (
                        <p className="empty-msg">データがありません</p>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <p className="empty-msg">データがありません</p>
              )}
            </div>
          )}

          {/* ===== キャンペーンタブ ===== */}
          {activeTab === "campaigns" && (
            <div className="tab-content">
              <div className="campaign-filter">
                {[
                  { value: "ALL", label: "すべて" },
                  { value: "ACTIVE", label: "配信中" },
                  { value: "PAUSED", label: "停止中" },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    className={`filter-chip ${
                      campaignFilter === opt.value ? "active" : ""
                    }`}
                    onClick={() => setCampaignFilter(opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {filteredCampaigns.length > 0 ? (
                <div className="campaigns-grid">
                  {filteredCampaigns.map((campaign) => {
                    const status =
                      STATUS_LABELS[campaign.status] || STATUS_LABELS.ARCHIVED;
                    const insights = campaign.insights || {};
                    return (
                      <div key={campaign.id} className="campaign-card">
                        <div className="campaign-card-header">
                          <div className="campaign-name">
                            <h4>{campaign.name}</h4>
                            <p className="campaign-meta">
                              <span className={`status-badge ${status.className}`}>
                                <span className="status-dot"></span>
                                {status.label}
                              </span>
                              {campaign.objective && (
                                <span className="objective-badge">
                                  {OBJECTIVE_LABELS[campaign.objective] ||
                                    campaign.objective}
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="campaign-budget">
                            <p className="budget-label">日予算</p>
                            <p className="budget-value">
                              {campaign.daily_budget
                                ? formatCurrency(campaign.daily_budget)
                                : "-"}
                            </p>
                          </div>
                        </div>

                        <div className="campaign-period">
                          <i className="fa-solid fa-calendar"></i>
                          {campaign.start_time
                            ? new Date(campaign.start_time).toLocaleDateString(
                                "ja-JP"
                              )
                            : "-"}{" "}
                          〜{" "}
                          {campaign.stop_time
                            ? new Date(campaign.stop_time).toLocaleDateString(
                                "ja-JP"
                              )
                            : "継続中"}
                        </div>

                        <div className="campaign-metrics">
                          <div className="metric">
                            <p className="metric-label">広告費</p>
                            <p className="metric-value">
                              {formatCurrency(insights.spend)}
                            </p>
                          </div>
                          <div className="metric">
                            <p className="metric-label">インプレッション</p>
                            <p className="metric-value">
                              {formatNumber(insights.impressions)}
                            </p>
                          </div>
                          <div className="metric">
                            <p className="metric-label">クリック</p>
                            <p className="metric-value">
                              {formatNumber(insights.clicks)}
                            </p>
                          </div>
                          <div className="metric">
                            <p className="metric-label">CV</p>
                            <p className="metric-value highlight">
                              {insights.conversions || 0}
                            </p>
                          </div>
                          <div className="metric">
                            <p className="metric-label">CTR</p>
                            <p className="metric-value">
                              {(insights.ctr || 0).toFixed(2)}%
                            </p>
                          </div>
                          <div className="metric">
                            <p className="metric-label">CPC</p>
                            <p className="metric-value">
                              {formatCurrency(insights.cpc)}
                            </p>
                          </div>
                          <div className="metric">
                            <p className="metric-label">CVR</p>
                            <p className="metric-value">
                              {(insights.conversion_rate || 0).toFixed(2)}%
                            </p>
                          </div>
                          <div className="metric">
                            <p className="metric-label">ROAS</p>
                            <p
                              className={`metric-value ${
                                insights.roas >= 3
                                  ? "good"
                                  : insights.roas >= 2
                                  ? "ok"
                                  : insights.roas > 0
                                  ? "warn"
                                  : ""
                              }`}
                            >
                              {insights.roas ? `${insights.roas}x` : "-"}
                            </p>
                          </div>
                        </div>

                        {/* クリエイティブ展開 */}
                        <CreativeSection
                          campaignId={campaign.id}
                          state={creativeStates[campaign.id]}
                          onToggle={toggleCreatives}
                          onSortChange={setCreativeSort}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="empty-msg">キャンペーンがありません</p>
              )}
            </div>
          )}

          {/* ===== 日別データタブ ===== */}
          {activeTab === "daily" && (
            <div className="tab-content">
              {dailyBreakdown.length > 0 && summary ? (
                <div className="daily-table-wrapper">
                  <table className="daily-table">
                    <thead>
                      <tr>
                        <th>日付</th>
                        <th>広告費</th>
                        <th>インプレッション</th>
                        <th>クリック</th>
                        <th>コンバージョン</th>
                        <th>CTR</th>
                        <th>CPC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...dailyBreakdown].reverse().map((day) => {
                        const date = new Date(day.date);
                        const isWeekend =
                          date.getDay() === 0 || date.getDay() === 6;
                        return (
                          <tr key={day.date} className={isWeekend ? "weekend" : ""}>
                            <td className="date-cell">
                              <span className="date-main">
                                {date.toLocaleDateString("ja-JP", {
                                  month: "numeric",
                                  day: "numeric",
                                })}
                              </span>
                              <span className="date-sub">
                                {
                                  ["日", "月", "火", "水", "木", "金", "土"][
                                    date.getDay()
                                  ]
                                }
                              </span>
                            </td>
                            <td>{formatCurrency(day.spend)}</td>
                            <td>{(day.impressions || 0).toLocaleString()}</td>
                            <td>{(day.clicks || 0).toLocaleString()}</td>
                            <td className="cv-cell">{day.conversions || 0}</td>
                            <td>{(day.ctr || 0).toFixed(2)}%</td>
                            <td>{formatCurrency(day.cpc)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>合計</td>
                        <td>{formatCurrency(summary.spend)}</td>
                        <td>{(summary.impressions || 0).toLocaleString()}</td>
                        <td>{(summary.clicks || 0).toLocaleString()}</td>
                        <td>{summary.conversions || 0}</td>
                        <td>{(summary.ctr || 0).toFixed(2)}%</td>
                        <td>{formatCurrency(summary.cpc)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="empty-msg">日別データがありません</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ===== クリエイティブセクション =====
function CreativeSection({ campaignId, state, onToggle, onSortChange }) {
  const isOpen = !!state?.open;
  const sortBy = state?.sortBy || "spend";

  const sortedCreatives = useMemo(() => {
    if (!state?.data) return [];
    return [...state.data].sort((a, b) => {
      const av = a.insights?.[sortBy] || 0;
      const bv = b.insights?.[sortBy] || 0;
      // CPCは小さい方が良いので昇順
      if (sortBy === "cpc") return av - bv;
      return bv - av;
    });
  }, [state?.data, sortBy]);

  const maxSpend = useMemo(() => {
    if (!sortedCreatives.length) return 0;
    return Math.max(...sortedCreatives.map((c) => c.insights?.spend || 0));
  }, [sortedCreatives]);

  return (
    <div className="creative-section">
      <button
        className={`creative-toggle ${isOpen ? "open" : ""}`}
        onClick={() => onToggle(campaignId)}
      >
        <i className="fa-solid fa-images"></i>
        <span>クリエイティブ別パフォーマンス</span>
        {state?.data && (
          <span className="creative-count">{state.data.length}</span>
        )}
        <i
          className={`fa-solid fa-chevron-down chevron ${isOpen ? "rotate" : ""}`}
        ></i>
      </button>

      {isOpen && (
        <div className="creative-body">
          {state?.loading ? (
            <div className="creative-loading">
              <div className="skeleton-row" />
              <div className="skeleton-row" />
            </div>
          ) : state?.error ? (
            <div className="creative-error">
              <i className="fa-solid fa-triangle-exclamation"></i>
              {state.error}
            </div>
          ) : sortedCreatives.length === 0 ? (
            <p className="empty-msg">クリエイティブがありません</p>
          ) : (
            <>
              <div className="creative-sort-bar">
                <span className="sort-label">
                  <i className="fa-solid fa-sort"></i>
                  並び替え:
                </span>
                {CREATIVE_SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    className={`sort-chip ${sortBy === opt.value ? "active" : ""}`}
                    onClick={() => onSortChange(campaignId, opt.value)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div className="creatives-grid">
                {sortedCreatives.map((ad, index) => {
                  const insights = ad.insights || {};
                  const creative = ad.creative || {};
                  const status =
                    STATUS_LABELS[ad.status] || STATUS_LABELS.ARCHIVED;
                  const spendRatio =
                    maxSpend > 0 ? (insights.spend / maxSpend) * 100 : 0;

                  return (
                    <div key={ad.id} className="creative-card">
                      <div className="creative-thumb">
                        {creative.thumbnail_url || creative.image_url ? (
                          <img
                            src={creative.thumbnail_url || creative.image_url}
                            alt={ad.name}
                            loading="lazy"
                          />
                        ) : creative.video_id ? (
                          <div className="creative-placeholder video">
                            <i className="fa-solid fa-circle-play"></i>
                          </div>
                        ) : (
                          <div className="creative-placeholder">
                            <i className="fa-solid fa-image"></i>
                          </div>
                        )}
                        <span className="creative-rank">#{index + 1}</span>
                        <span
                          className={`creative-status ${status.className}`}
                        >
                          <span className="status-dot"></span>
                          {status.label}
                        </span>
                      </div>

                      <div className="creative-content">
                        <div className="creative-info">
                          <p className="creative-name" title={ad.name}>
                            {ad.name}
                          </p>
                          {creative.title && (
                            <p className="creative-title">{creative.title}</p>
                          )}
                          {creative.body && (
                            <p className="creative-body-text">
                              {creative.body}
                            </p>
                          )}
                        </div>

                        <div className="creative-spend-bar">
                          <div
                            className="bar-fill"
                            style={{ width: `${spendRatio}%` }}
                          ></div>
                        </div>

                        <div className="creative-metrics">
                          <div className="c-metric">
                            <span className="c-label">広告費</span>
                            <span className="c-value">
                              {formatCurrency(insights.spend)}
                            </span>
                          </div>
                          <div className="c-metric">
                            <span className="c-label">IMP</span>
                            <span className="c-value">
                              {formatNumber(insights.impressions)}
                            </span>
                          </div>
                          <div className="c-metric">
                            <span className="c-label">CLK</span>
                            <span className="c-value">
                              {formatNumber(insights.clicks)}
                            </span>
                          </div>
                          <div className="c-metric">
                            <span className="c-label">CV</span>
                            <span className="c-value highlight">
                              {insights.conversions || 0}
                            </span>
                          </div>
                          <div className="c-metric">
                            <span className="c-label">CTR</span>
                            <span className="c-value">
                              {(insights.ctr || 0).toFixed(2)}%
                            </span>
                          </div>
                          <div className="c-metric">
                            <span className="c-label">CPC</span>
                            <span className="c-value">
                              {formatCurrency(insights.cpc)}
                            </span>
                          </div>
                          <div className="c-metric">
                            <span className="c-label">CVR</span>
                            <span className="c-value">
                              {(insights.conversion_rate || 0).toFixed(2)}%
                            </span>
                          </div>
                          <div className="c-metric">
                            <span className="c-label">ROAS</span>
                            <span
                              className={`c-value ${
                                insights.roas >= 3
                                  ? "good"
                                  : insights.roas >= 2
                                  ? "ok"
                                  : insights.roas > 0
                                  ? "warn"
                                  : ""
                              }`}
                            >
                              {insights.roas ? `${insights.roas}x` : "-"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default MetaAdsPage;
